import type React from "react";

import DateField from "@/components/ui/date-field";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TabsContent } from "@/components/ui/tabs";
import { Separator } from "@radix-ui/react-select";
import { Eye, EyeIcon as EyeClosed } from "lucide-react";
import { useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { IMaskInput } from "react-imask";
import type { DriverFormValues } from "./form";

interface DriverDataTabProps {
  form: UseFormReturn<DriverFormValues>;
  isIbutton: boolean;
  setIsIbutton: (value: boolean) => void;
}

const DriverDataTab = ({
  form,
  isIbutton,
  setIsIbutton,
}: DriverDataTabProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleIbuttonCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsIbutton(e.target.checked);
    const currentValue = form.getValues("driver_data.serial");
    if (e.target.checked) {
      form.setValue("driver_data.ibutton", currentValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      form.setValue("driver_data.ibutton", null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const isHex = (str: string) => /^(0x)?[0-9a-f]*[a-f][0-9a-f]*$/i.test(str);

  // Automatically set iButton field when serial is a valid hex
  const serialValue = form.watch("driver_data.serial");
  useEffect(() => {
    const input = form.getValues("driver_data.serial")?.trim();
    if (!input) return;
    if (form.getValues("driver_data.ibutton")) return;
    if (isHex(input)) {
      setIsIbutton(true);
      form.setValue("driver_data.ibutton", input, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      setIsIbutton(false);
      form.setValue("driver_data.ibutton", null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [serialValue, form, setIsIbutton]);

  return (
    <TabsContent value="driver_data" className="space-y-6 mt-4">
      <FormField
        control={form.control}
        name="driver_data.status"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Status</FormLabel>
            <Select
              onValueChange={(value) => field.onChange(Number(value))}
              value={String(field.value)}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="1">Ativo</SelectItem>
                <SelectItem value="0">Inativo</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="driver_data.name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Nome</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="driver_data.cpf"
        render={({ field }) => (
          <FormItem>
            <FormLabel>CPF</FormLabel>
            <FormControl>
              <IMaskInput
                mask="000.000.000-00"
                unmask
                value={field.value ?? ""}
                // O IMask dispara onAccept ao montar; repassar o mesmo valor
                // revalidaria o campo e apagaria o erro 422 vindo do backend.
                onAccept={(value: string) => {
                  if (value !== (field.value ?? "")) {
                    field.onChange(value || null);
                  }
                }}
                onBlur={field.onBlur}
                inputRef={field.ref}
                name={field.name}
                inputMode="numeric"
                placeholder="000.000.000-00"
                data-testid="driver-cpf-input"
                className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="driver_data.serial"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Código do cartão</FormLabel>
            <FormControl>
              <Input
                {...field}
                value={field.value ?? ""}
                onChange={(e) => {
                  field.onChange(e);
                  if (isIbutton) {
                    form.setValue("driver_data.ibutton", e.target.value);
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="driver_data.ibutton"
        render={() => (
          <FormItem>
            <FormControl>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={isIbutton}
                  onChange={handleIbuttonCheckboxChange}
                  className="mr-2"
                />
                <label
                  htmlFor="ibutton"
                  className="hover:cursor-pointer"
                  onClick={() => {
                    const newValue = !isIbutton;
                    setIsIbutton(newValue);
                    if (newValue) {
                      form.setValue(
                        "driver_data.ibutton",
                        form.getValues("driver_data.serial"),
                      );
                    } else {
                      form.setValue("driver_data.ibutton", null);
                    }
                  }}
                >
                  Usar como iButton
                </label>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="driver_data.type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Condutor</FormLabel>
            <Select
              value={String(field.value)}
              onValueChange={(value) => field.onChange(Number(value))}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo de condutor" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="1">Veículos</SelectItem>
                <SelectItem value="2">Máquinas</SelectItem>
                <SelectItem value="3">Ambos</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="driver_data.enable_notifications"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
            <div
              className="space-y-0.5 hover:cursor-pointer"
              onClick={() => field.onChange(!field.value)}
            >
              <FormLabel
                className="text-base hover:cursor-pointer"
                htmlFor="enable_notifications"
              >
                Habilitar notificações
              </FormLabel>
              <FormDescription>
                Receber notificações do sistema, como excessos de velocidade,
                falha de alimentação, etc.
              </FormDescription>
            </div>
            <FormControl>
              <Switch
                className="bg-primary-500"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="driver_data.drivers_license"
        render={({ field }) => (
          <FormItem>
            <FormLabel>CNH</FormLabel>
            <FormControl>
              <Input {...field} value={field.value ?? ""} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <DateField
        form={form}
        label="Vencimento da CNH"
        name="driver_data.drivers_license_expiration"
      />

      <FormField
        control={form.control}
        name="driver_data.drivers_license_category"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Categoria da CNH</FormLabel>
            <Select
              value={String(field.value)}
              onValueChange={(value) => field.onChange(Number(value))}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a categoria da CNH" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="1">A - Motocicletas</SelectItem>
                <SelectItem value="2">B - Automóvel</SelectItem>
                <SelectItem value="3">C - Caminhões</SelectItem>
                <SelectItem value="4">D - Ônibus</SelectItem>
                <SelectItem value="5">E - Carretas</SelectItem>
                <SelectItem value="6">AB - Moticletas e Automóveis</SelectItem>
                <SelectItem value="7">AC - Moticletas e Caminhões</SelectItem>
                <SelectItem value="8">AD - Moticletas e Ônibus</SelectItem>
                <SelectItem value="9">AE - Moticletas e Carretas</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <DateField
        form={form}
        label="Data do último exame toxicológico"
        name="driver_data.toxicological_exam_date"
      />

      <DateField
        form={form}
        label="Data do último exame médico"
        name="driver_data.medical_exam_date"
      />

      <div>
        <Separator className="mb-6" />
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium">Dados de acesso</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Os dados de acesso são opcionais e só precisam ser informados caso
              o motorista precise acessar o sistema.
            </p>
          </div>

          <FormField
            control={form.control}
            name="driver_data.user"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Usuário</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    autoComplete="off"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="driver_data.password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Senha</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => {
                        const value =
                          e.target.value.trim() === "" ? null : e.target.value;
                        field.onChange(value);
                        if (!value) {
                          form.clearErrors("driver_data.password");
                          form.resetField("driver_data.password", {
                            defaultValue: undefined,
                            keepDirty: false,
                            keepTouched: false,
                            keepError: false,
                          });
                        }
                      }}
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={toggleShowPassword}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                    >
                      {showPassword ? <EyeClosed /> : <Eye />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </TabsContent>
  );
};

export default DriverDataTab;
